import LanguageSelector from "./LanguageSelector";
import LoginButton from "./LoginButton";
import { useLoginAndRegisterStore } from "../stores/LoginAndRegisterStore";
import { useUserStore } from "../stores/UserStore";
import { useTranslation } from "react-i18next";
import { Avatar } from "../ui/Gearavatar";
import { Link, useLocation } from "react-router-dom";
import { useFiltersStore } from "../stores/FiltersStore";
import FiltersButton from "./FiltersButton";
import { useState } from "react";
import {
  FaTimes,
  FaUser,
  FaInfoCircle,
  FaSignOutAlt,
  FaBars,
  FaUserPlus,
} from "react-icons/fa";

// Helper component for mobile drawer buttons
const MobileDrawerButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}> = ({ icon, label, onClick }) => (
  <button
    onClick={onClick}
    className="group flex items-center justify-between p-4 rounded-2xl bg-white/5 hover:bg-[#5A18C4]/20 border border-transparent hover:border-[#5A18C4]/50 transition-all duration-300 w-full text-left"
  >
    <div className="flex items-center gap-4">
      <div className="w-10 h-10 rounded-full bg-[#5A18C4]/20 flex items-center justify-center group-hover:bg-[#5A18C4] transition-colors">
        {icon}
      </div>
      <span className="text-lg font-medium tracking-wide">{label}</span>
    </div>
  </button>
);

export const NavBar = () => {
  const { user, logout } = useUserStore();
  const { t } = useTranslation();
  const location = useLocation();
  const {
    isOpen,
    setIsOpen,
    selectedTypes,
    zipCode,
    selectedCategory,
    selectedSubcategory,
    selectedType,
  } = useFiltersStore();
  const isMapPage = location.pathname === "/";
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Calcola il numero di filtri attivi (escludendo searchRadius)
  const activeFiltersCount = [
    selectedTypes.length > 0,
    zipCode.trim() !== "",
    selectedCategory !== "",
    selectedSubcategory !== "",
    selectedType !== "",
  ].filter(Boolean).length;

  // Drawer Animation classes
  const drawerClasses = `fixed top-0 right-0 z-[2002] h-full w-[80%] max-w-[300px] bg-[#2D1B4E] shadow-2xl transform transition-transform duration-300 ease-in-out ${
    isDrawerOpen ? "translate-x-0" : "translate-x-full"
  }`;

  // Backdrop
  const backdropClasses = `fixed inset-0 z-[2001] bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
    isDrawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"
  }`;

  return (
    <>
      <div
        className="fixed top-0 flex items-center justify-between px-4 md:px-6 left-0 right-0 z-[1001] h-[70px]"
        style={{
          background:
            "linear-gradient(90deg, #2F0D68 0%, #240D49 50%, #2F0D68 100%)",
          borderBottom: "1px solid #5A18C44D",
        }}
      >
        <div className="flex w-full items-center justify-between">
          <Link to="/">
            <img
              src="/gear-surf.svg"
              alt="Logo"
              className="brightness-0 w-[130px] md:w-[161px] invert -mb-1 cursor-pointer"
            />
          </Link>
          <div className="flex items-center gap-2 md:gap-6">
            {/* Desktop About Link */}
            <Link
              to="/about"
              className="text-white text-[16px] hover:opacity-80 transition-opacity hidden md:block"
            >
              {t("navigation.about", "About")}
            </Link>

            {user && (
              <>
                {/* Desktop Profile Link */}
                <Link
                  to="/profile"
                  className="hidden md:flex items-center gap-2 hover:opacity-80 transition-opacity"
                >
                  <Avatar
                    size={32}
                    name={user.firstName}
                    surname={user.lastName}
                  />
                  <span className="text-white text-[16px] capitalize">
                    {user.firstName} {user.lastName}
                  </span>
                </Link>

                {/* Mobile Profile Button (Opens Drawer) */}
                <button
                  onClick={() => setIsDrawerOpen(true)}
                  className="flex md:hidden items-center gap-2 hover:opacity-80 transition-opacity"
                >
                  <Avatar
                    size={32}
                    name={user.firstName}
                    surname={user.lastName}
                  />
                </button>
              </>
            )}

            {!user && (
              <>
                {/* Desktop Login Button */}
                <div className="block">
                  <LoginButton
                    currentUser={user?.email}
                    onLogin={console.log}
                  />
                </div>

                {/* Mobile Menu Button (Opens Drawer) */}
                <button
                  onClick={() => setIsDrawerOpen(true)}
                  className="flex md:hidden items-center justify-center w-[43px] h-[33px] bg-primary text-white rounded-[10px] hover:opacity-80 transition-opacity"
                >
                  <FaBars size={18} />
                </button>
              </>
            )}

            {user && (
              // Desktop Logout Button
              <button
                onClick={() => logout()}
                className="hidden md:flex items-center gap-2 text-white hover:opacity-80 transition-opacity"
              >
                <img
                  src={"/icons/logout.svg"}
                  alt="Logout"
                  className="ml-[-4px] min-w-3 min-h-3 object-contain"
                />
                <span>{t("navigation.logout")}</span>
              </button>
            )}

            {/* Desktop Language Selector */}
            <div className="hidden md:block">
              <LanguageSelector />
            </div>

            <FiltersButton
              //@ts-expect-error boh
              isMapPage={isMapPage}
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              activeFiltersCount={activeFiltersCount}
            />
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      <div className={backdropClasses} onClick={() => setIsDrawerOpen(false)} />
      <div
        className={`fixed top-0 right-0 z-[2002] h-full w-[85%] max-w-[320px] bg-gradient-to-b from-[#2F0D68] to-[#120524] shadow-2xl transform transition-transform duration-300 ease-out border-l border-white/10 ${
          isDrawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full text-white p-6 relative">
          {/* Close Button */}
          <button
            onClick={() => setIsDrawerOpen(false)}
            className="absolute top-6 right-6 p-2 rounded-full hover:bg-white/10 transition-colors z-10"
          >
            <FaTimes size={20} className="text-white/80 hover:text-white" />
          </button>

          {/* User Header - Only when logged in */}
          {user && (
            <div className="flex flex-col items-center mt-8 mb-10 fade-in">
              <div className="p-1 rounded-full border-2 border-[#5A18C4] mb-3 shadow-[0_0_15px_rgba(90,24,196,0.5)]">
                <Avatar
                  size={80}
                  name={user.firstName}
                  surname={user.lastName}
                />
              </div>
              <h2 className="text-2xl font-bold capitalize mt-2">
                {user.firstName} {user.lastName}
              </h2>
              <p className="text-sm text-white/60">{user.email}</p>
            </div>
          )}

          {/* Logo Header - Only when not logged in */}
          {!user && (
            <div className="flex flex-col items-center mt-8 mb-10">
              <img
                src="/gear-surf.svg"
                alt="Logo"
                className="brightness-0 invert w-[140px] mb-4"
              />
            </div>
          )}

          {/* Navigation Links */}
          <div className="flex flex-col gap-3 flex-1">
            {/* Profile Link - Only when logged in */}
            {user && (
              <Link
                to="/profile"
                className="group flex items-center justify-between p-4 rounded-2xl bg-white/5 hover:bg-[#5A18C4]/20 border border-transparent hover:border-[#5A18C4]/50 transition-all duration-300"
                onClick={() => setIsDrawerOpen(false)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#5A18C4]/20 flex items-center justify-center group-hover:bg-[#5A18C4] transition-colors">
                    <FaUser
                      className="text-[#9d72e8] group-hover:text-white transition-colors"
                      size={16}
                    />
                  </div>
                  <span className="text-lg font-medium tracking-wide">
                    {t("navigation.profile", "Profile")}
                  </span>
                </div>
              </Link>
            )}

            {/* Login Button - Only when not logged in */}
            {!user && (
              <MobileDrawerButton
                icon={
                  <img
                    src="/icons/login.svg"
                    alt="Login"
                    className="h-5 w-5 object-contain brightness-0 invert opacity-80"
                  />
                }
                label={t("navigation.login", "Login")}
                onClick={() => {
                  setIsDrawerOpen(false);
                  useLoginAndRegisterStore.getState().openLogin();
                }}
              />
            )}

            {/* About Link - Always visible */}
            <Link
              to="/about"
              className="group flex items-center justify-between p-4 rounded-2xl bg-white/5 hover:bg-[#5A18C4]/20 border border-transparent hover:border-[#5A18C4]/50 transition-all duration-300"
              onClick={() => setIsDrawerOpen(false)}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#5A18C4]/20 flex items-center justify-center group-hover:bg-[#5A18C4] transition-colors">
                  <FaInfoCircle
                    className="text-[#9d72e8] group-hover:text-white transition-colors"
                    size={16}
                  />
                </div>
                <span className="text-lg font-medium tracking-wide">
                  {t("navigation.about", "About")}
                </span>
              </div>
            </Link>

            {/* Language Selector - Always visible */}
            <div className="group flex items-center justify-between p-4 rounded-2xl bg-white/5 border border-transparent transition-all duration-300">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#5A18C4]/20 flex items-center justify-center">
                  <img
                    src={"/icons/world.svg"}
                    alt="Language"
                    className="h-5 w-5 object-contain brightness-0 invert opacity-80"
                  />
                </div>
                <span className="text-lg font-medium tracking-wide">
                  Language
                </span>
              </div>
              <div className="scale-90 origin-right">
                <LanguageSelector />
              </div>
            </div>
          </div>

          {/* Footer / Logout - Only when logged in */}
          {user && (
            <div className="mt-auto pt-6 border-t border-white/10">
              <button
                onClick={() => {
                  logout();
                  setIsDrawerOpen(false);
                }}
                className="w-full flex items-center justify-center gap-3 p-4 rounded-2xl text-[#ff7c67] bg-[#ff7c67]/10 hover:bg-[#ff7c67]/20 border border-[#ff7c67]/20 hover:border-[#ff7c67]/50 transition-all duration-300"
              >
                <FaSignOutAlt size={18} />
                <span className="font-semibold text-lg">
                  {t("navigation.logout", "Logout")}
                </span>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
